"use client";

import * as React from "react";
import { resolveEnvironment } from "../../../lib/environment";
import {
  type AppsConfig,
  useRemoteConfigValue,
} from "../../../lib/remote-config";
import type { GlobalMenu } from "../../app-launcher/types";
import { isInAppBrowser } from "../../../lib/utils";
import { TrackingBoundary } from "../../../lib/sentry";
import {
  BridgeFlow,
  type BridgeGetToTokensFn,
  type BridgeSubmittedInfo,
} from "../../bridge";
import { RelayDeposit } from "../../relay";
import { ONEUSD_LOGO } from "../../relay/lib/token-logos";
import {
  ResponsiveShell,
  ShellContent,
} from "../../relay/components/shell/ResponsiveShell";
import {
  isGetOneUsdTargetAvailable,
  resolveGetOneUsdRoute,
} from "../lib/token-refs";
import {
  isGetOneUsdActionEnabled,
  type GetOneUsdRemoteModes,
} from "../lib/action-modes";
import {
  isOneUsdHostname,
  ONE_USD_SITE_URLS,
  resolveOneUsdSiteUrl,
} from "../lib/one-usd-site";
import { useGetOneUsdApr } from "../hooks/use-get-one-usd-apr";
import { formatCrossdAprPercent } from "../../wallet-info/queries/use-crossd-apr";
import {
  getTransactionPendingLabel,
  getTransactionSuccessNote,
} from "../lib/transaction-status";
import type {
  GetOneUsdActionId,
  GetOneUsdEvent,
  GetOneUsdProps,
} from "../types";
import {
  BridgeActionIcon,
  CloseActionIcon,
  SwapActionIcon,
  TransferActionIcon,
} from "./icons";
import "../styles/component.css";

const ACTIONS: Array<{
  id: GetOneUsdActionId;
  label: string;
  eta?: string;
  Icon: typeof SwapActionIcon;
}> = [
  { id: "swap", label: "Swap", eta: "Instant", Icon: SwapActionIcon },
  { id: "bridge", label: "Bridge", Icon: BridgeActionIcon },
  {
    id: "transfer",
    label: "Transfer Crypto",
    eta: "QR deposit",
    Icon: TransferActionIcon,
  },
];

type TransactionStatusPhase = "pending" | "success" | "failure";

interface TransactionStatus {
  id: number;
  phase: TransactionStatusPhase;
  action: "swap" | "bridge";
  label: string;
  txHash: string;
}

const DEFAULT_TRANSACTION_RESULT_DURATION_MS = 4_000;

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return typeof error === "string" ? error : "Unknown error";
}

function callSafely<T>(callback: ((value: T) => void) | undefined, value: T) {
  try {
    callback?.(value);
  } catch {
    // Consumer callbacks must not interrupt transaction state tracking.
  }
}

function useCollapsedTrigger({
  compactTrigger,
  collapseOnScroll,
  scrollThreshold,
}: Pick<
  GetOneUsdProps,
  "compactTrigger" | "collapseOnScroll" | "scrollThreshold"
>): boolean {
  const [inApp, setInApp] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    setInApp(isInAppBrowser());
  }, []);

  React.useEffect(() => {
    if (collapseOnScroll === false) {
      setScrolled(false);
      return;
    }
    const update = () => setScrolled(window.scrollY > (scrollThreshold ?? 200));
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, [collapseOnScroll, scrollThreshold]);

  return Boolean(compactTrigger || inApp || scrolled);
}

function RouteUnavailable({ loading }: { loading: boolean }) {
  return (
    <div className="gou-route-state" role="status">
      {loading && <span className="gou-spinner" aria-hidden="true" />}
      <h3>{loading ? "Preparing route" : "Route unavailable"}</h3>
      <p>
        {loading
          ? "Loading token and contract configuration."
          : "The configured ONEUSD route is not available in this environment."}
      </p>
    </div>
  );
}

export function GetOneUsd({
  walletAddress,
  tokens,
  pairs,
  loading = false,
  bridge,
  relay,
  env,
  targetSymbol: targetSymbolProp,
  relayTargetSymbol = "ONEUSD",
  targetIconUrl,
  mode = "floating",
  buttonComponent,
  theme = "dark",
  mobileBreakpoint = 768,
  drawerDirection = "bottom",
  dialogWidth = "480px",
  drawerMaxWidth,
  drawerMinWidth,
  style,
  className,
  contentClassName,
  hidden = false,
  compactTrigger,
  collapseOnScroll = true,
  scrollThreshold = 200,
  waitForTransaction,
  transactionResultDurationMs = DEFAULT_TRANSACTION_RESULT_DURATION_MS,
  onRequestConnect,
  onEvent,
}: GetOneUsdProps) {
  const [chooserOpen, setChooserOpen] = React.useState(false);
  const [activeAction, setActiveAction] =
    React.useState<GetOneUsdActionId | null>(null);
  const [relayMounted, setRelayMounted] = React.useState(false);
  const [transactionStatus, setTransactionStatus] =
    React.useState<TransactionStatus | null>(null);
  const [showLearnMore, setShowLearnMore] = React.useState(false);
  const transactionIdRef = React.useRef(0);
  const transactionLockedRef = React.useRef(false);
  const resultTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = React.useRef(true);
  const iconOnly = useCollapsedTrigger({
    compactTrigger,
    collapseOnScroll,
    scrollThreshold,
  });
  const resolvedEnv = React.useMemo(() => resolveEnvironment(env), [env]);
  const remoteModes = useRemoteConfigValue<
    AppsConfig,
    GetOneUsdRemoteModes
  >("apps.json", resolvedEnv, (config) => config?.getOneUsd?.modes);
  const remoteOneUsdSiteUrl = useRemoteConfigValue<GlobalMenu, string>(
    "app-launcher-v2.json",
    resolvedEnv,
    (config) => resolveOneUsdSiteUrl(config, resolvedEnv),
  );
  const oneUsdSiteUrl = remoteOneUsdSiteUrl ?? ONE_USD_SITE_URLS[resolvedEnv];
  const availableActions = React.useMemo(
    () => ACTIONS.filter(({ id }) => isGetOneUsdActionEnabled(id, remoteModes)),
    [remoteModes],
  );
  const hasEnabledActions = availableActions.length > 0;
  const aprPercent = useGetOneUsdApr(!hidden && hasEnabledActions);

  const swapRoute = React.useMemo(
    () => resolveGetOneUsdRoute(tokens, pairs, "swap"),
    [pairs, tokens],
  );
  const bridgeRoute = React.useMemo(
    () => resolveGetOneUsdRoute(tokens, pairs, "bridge"),
    [pairs, tokens],
  );
  const targetToken = swapRoute.targetToken ?? bridgeRoute.targetToken;
  const targetSymbol = targetSymbolProp ?? targetToken?.symbol ?? "ONEUSD";
  const iconUrl = targetIconUrl ?? targetToken?.iconUrl ?? ONEUSD_LOGO;
  const triggerIconOnly = mode === "floating" && iconOnly;
  const TriggerComponent =
    mode === "button" && buttonComponent ? buttonComponent : "button";

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
    };
  }, []);

  React.useEffect(() => {
    setShowLearnMore(!isOneUsdHostname(window.location.hostname));
  }, []);

  React.useEffect(() => {
    if (!hasEnabledActions) setChooserOpen(false);
  }, [hasEnabledActions]);

  const emit = React.useCallback(
    (event: GetOneUsdEvent) => onEvent?.(event),
    [onEvent],
  );

  const openChooser = React.useCallback(() => {
    if (transactionStatus || !hasEnabledActions) return;
    emit({
      name: "fab_click",
      state: triggerIconOnly ? "icon_only" : "expanded",
    });
    setChooserOpen(true);
  }, [emit, hasEnabledActions, transactionStatus, triggerIconOnly]);

  const selectAction = React.useCallback(
    (action: GetOneUsdActionId) => {
      if (transactionStatus || !isGetOneUsdActionEnabled(action, remoteModes)) {
        return;
      }
      emit({ name: "action_select", action });
      setChooserOpen(false);
      if (action === "transfer") setRelayMounted(true);
      setActiveAction(action);
    },
    [emit, remoteModes, transactionStatus],
  );

  const requestConnect = React.useCallback(
    (action: GetOneUsdActionId) => {
      emit({ name: "connect_click", action });
      void Promise.resolve(onRequestConnect?.()).catch((error) => {
        emit({ name: "failure", action, message: errorMessage(error) });
      });
    },
    [emit, onRequestConnect],
  );

  const bridgeAction =
    activeAction === "swap" || activeAction === "bridge"
      ? activeAction
      : null;
  const route = bridgeAction === "swap" ? swapRoute : bridgeRoute;
  const routeReady = Boolean(
    pairs && route.targetToken && route.fromTokens.length > 0,
  );

  const getToTokens = React.useMemo<BridgeGetToTokensFn | undefined>(() => {
    if (!route.targetToken) return undefined;
    const target = route.targetToken;
    return (fromToken) => {
      const available = bridge.getToTokens?.(fromToken, tokens);
      if (available && !isGetOneUsdTargetAvailable(available, target)) return [];
      return [target];
    };
  }, [bridge.getToTokens, route.targetToken, tokens]);

  const {
    onSubmitted: onBridgeSubmitted,
    onSuccess: onBridgeSuccess,
    onFailed: onBridgeFailed,
    ...bridgeProps
  } = bridge;
  const {
    onDeposited: onRelayDeposited,
    onError: onRelayError,
    ...relayProps
  } = relay ?? {};

  const showTransactionResult = React.useCallback(
    (
      id: number,
      phase: "success" | "failure",
      action: "swap" | "bridge",
      label: string,
      txHash: string,
    ) => {
      if (!mountedRef.current || transactionIdRef.current !== id) return;
      setTransactionStatus({ id, phase, action, label, txHash });
      if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
      resultTimerRef.current = setTimeout(() => {
        if (!mountedRef.current || transactionIdRef.current !== id) return;
        transactionLockedRef.current = false;
        setTransactionStatus(null);
        resultTimerRef.current = null;
      }, Math.max(0, transactionResultDurationMs));
    },
    [transactionResultDurationMs],
  );

  const handleBridgeSubmitted = React.useCallback(
    (info: BridgeSubmittedInfo, action: "swap" | "bridge") => {
      if (transactionLockedRef.current) return;
      transactionLockedRef.current = true;
      const id = ++transactionIdRef.current;
      const pendingLabel = getTransactionPendingLabel(info, action);
      setTransactionStatus({
        id,
        phase: "pending",
        action,
        label: pendingLabel,
        txHash: info.txHash,
      });
      setChooserOpen(false);
      setActiveAction(null);
      callSafely(onBridgeSubmitted, info);

      void Promise.resolve()
        .then(() => waitForTransaction?.(info))
        .then(() => {
          if (!mountedRef.current || transactionIdRef.current !== id) return;
          showTransactionResult(
            id,
            "success",
            action,
            getTransactionSuccessNote(info, action),
            info.txHash,
          );
          emit({ name: "success", action, txHash: info.txHash });
        })
        .catch((error) => {
          if (!mountedRef.current || transactionIdRef.current !== id) return;
          const message = errorMessage(error);
          showTransactionResult(
            id,
            "failure",
            action,
            "Transaction failed",
            info.txHash,
          );
          callSafely(onBridgeFailed, { message, txHash: info.txHash });
          emit({ name: "failure", action, message });
        });
    },
    [
      emit,
      onBridgeFailed,
      onBridgeSubmitted,
      showTransactionResult,
      waitForTransaction,
    ],
  );

  return (
    <TrackingBoundary feature="get_one_usd">
      <div className={className} data-theme={theme} data-gou-mode={mode}>
        <ResponsiveShell
          theme={theme}
          mobileBreakpoint={mobileBreakpoint}
          drawerDirection={drawerDirection}
          dialogWidth={dialogWidth}
          drawerMaxWidth={drawerMaxWidth}
          drawerMinWidth={drawerMinWidth}
          open={chooserOpen}
          onOpenChange={setChooserOpen}
        >
          {!hidden &&
            hasEnabledActions &&
            (mode === "button" || !transactionStatus) && (
            <TriggerComponent
              type="button"
              className="gou-fab"
              style={style}
              data-trigger-mode={mode}
              data-icon-only={triggerIconOnly ? "true" : "false"}
              onClick={openChooser}
              disabled={Boolean(transactionStatus)}
              aria-label={`Get ${targetSymbol}`}
              data-track="fab_click"
            >
              {mode === "floating" && (
                <span className="gou-tooltip">Choose how to get {targetSymbol}</span>
              )}
              <span className="gou-fab-icon-frame">
                <img className="gou-fab-icon" src={iconUrl} alt="" />
              </span>
              <span className="gou-fab-label gou-fab-trigger-copy">
                <span>Get {targetSymbol}</span>
                {aprPercent !== null && (
                  <span className="gou-fab-apr">
                    {formatCrossdAprPercent(aprPercent)}
                  </span>
                )}
              </span>
            </TriggerComponent>
          )}
          {transactionStatus && (
            <div
              className="gou-fab gou-fab-status"
              style={mode === "floating" ? style : undefined}
              data-floating-status="true"
              data-icon-only="false"
              data-tx-phase={transactionStatus.phase}
              role="status"
              aria-live="polite"
              aria-label={
                transactionStatus.phase === "pending"
                  ? `${transactionStatus.label} transaction in progress`
                  : transactionStatus.label
              }
            >
              <span className="gou-fab-icon-frame">
                <img className="gou-fab-icon" src={iconUrl} alt="" />
                {transactionStatus.phase === "pending" && (
                  <span className="gou-fab-progress-ring" aria-hidden="true" />
                )}
              </span>
              <span className="gou-fab-label">
                {transactionStatus.phase === "pending" ? (
                  <>
                    {transactionStatus.label}
                    <span className="gou-fab-dots" aria-hidden="true">
                      <span>.</span><span>.</span><span>.</span>
                    </span>
                  </>
                ) : (
                  transactionStatus.label
                )}
              </span>
            </div>
          )}
          <ShellContent
            ariaTitle={`Get ${targetSymbol}`}
            className={`gou-shell gou-chooser ${contentClassName ?? ""}`}
          >
            <div className="gou-chooser-body">
              <button
                type="button"
                className="gou-close"
                onClick={() => setChooserOpen(false)}
                aria-label="Close"
              >
                <CloseActionIcon />
              </button>
              <header className="gou-intro">
                <h2>Get {targetSymbol}</h2>
                {showLearnMore && (
                  <a
                    className="gou-intro-link"
                    href={oneUsdSiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="What is ONEUSD? Opens in a new tab"
                    data-track="oneusd_learn_more"
                  >
                    What is ONEUSD? <span aria-hidden="true">↗</span>
                  </a>
                )}
              </header>
              <ul className="gou-actions">
                {availableActions.map(({ id, label, eta, Icon }) => {
                  const description =
                    id === "swap"
                      ? `Swap the ${swapRoute.fromTokens[0]?.symbol ?? "ONE"} you already hold`
                      : id === "bridge"
                        ? `Bridge USDT to ${targetSymbol}`
                        : "Deposit from any chain or exchange";
                  const feeDelegated =
                    id === "swap"
                      ? pairs?.swap.feeDelegated
                      : id === "bridge"
                        ? pairs?.bridge.feeDelegated
                        : false;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        className="gou-action"
                        onClick={() => selectAction(id)}
                        data-track="action_select"
                        data-track-id={id}
                      >
                        <span className="gou-action-icon"><Icon /></span>
                        <span className="gou-action-copy">
                          <span className="gou-action-title">
                            <span>{label}</span>
                            {feeDelegated && (
                              <span className="gou-badge">Fee Delegated</span>
                            )}
                          </span>
                          <span className="gou-action-description">{description}</span>
                        </span>
                        {eta && <span className="gou-action-eta">{eta}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </ShellContent>
        </ResponsiveShell>

        <ResponsiveShell
          theme={theme}
          mobileBreakpoint={mobileBreakpoint}
          drawerDirection={drawerDirection}
          dialogWidth={dialogWidth}
          drawerMaxWidth={drawerMaxWidth}
          drawerMinWidth={drawerMinWidth}
          open={Boolean(bridgeAction)}
          onOpenChange={(open) => {
            if (!open) setActiveAction(null);
          }}
        >
          <ShellContent
            ariaTitle={`${bridgeAction === "bridge" ? "Bridge" : "Swap"} to ${targetSymbol}`}
            className={`gou-shell gou-execution ${contentClassName ?? ""}`}
          >
            {!routeReady ? (
              <RouteUnavailable loading={loading || !pairs} />
            ) : (
              <BridgeFlow
                key={`${bridgeAction}:${route.fromTokens.map((token) => `${token.chainId}:${token.address}`).join(",")}`}
                {...bridgeProps}
                env={env}
                variant="embedded"
                title={`${bridgeAction === "bridge" ? "Bridge" : "Swap"} to ${targetSymbol}`}
                walletAddress={walletAddress ?? ""}
                isConnected={Boolean(walletAddress)}
                onRequestConnect={() => requestConnect(bridgeAction ?? "swap")}
                tokens={route.fromTokens}
                initialFromToken={route.fromTokens[0]}
                initialToToken={route.targetToken}
                getToTokens={getToTokens}
                onClose={() => setActiveAction(null)}
                onBackToWallet={() => setActiveAction(null)}
                onSubmitted={(info) => {
                  handleBridgeSubmitted(info, bridgeAction ?? "swap");
                }}
                onSuccess={(info) => {
                  callSafely(onBridgeSuccess, info);
                }}
                onFailed={(info) => {
                  callSafely(onBridgeFailed, info);
                  emit({
                    name: "failure",
                    action: bridgeAction ?? "swap",
                    message: info.message,
                  });
                }}
              />
            )}
          </ShellContent>
        </ResponsiveShell>

        {relayMounted && (
          <RelayDeposit
            {...relayProps}
            environment={relayProps.environment ?? env}
            theme={relayProps.theme ?? theme}
            mobileBreakpoint={relayProps.mobileBreakpoint ?? mobileBreakpoint}
            drawerDirection={relayProps.drawerDirection ?? drawerDirection}
            dialogWidth={relayProps.dialogWidth ?? dialogWidth}
            drawerMaxWidth={relayProps.drawerMaxWidth ?? drawerMaxWidth}
            drawerMinWidth={relayProps.drawerMinWidth ?? drawerMinWidth}
            walletAddress={walletAddress}
            open={activeAction === "transfer"}
            onOpenChange={(open) => {
              if (!open) setActiveAction(null);
            }}
            requireWalletConnection
            showRecovery={relayProps.showRecovery ?? true}
            showRecipientInput={false}
            hideAmount
            targetTokens={[relayTargetSymbol]}
            defaultTarget={relayTargetSymbol}
            onRequestConnect={() => requestConnect("transfer")}
            onDeposited={(txHash) => {
              onRelayDeposited?.(txHash);
              emit({ name: "success", action: "transfer", txHash });
            }}
            onError={(error) => {
              onRelayError?.(error);
              emit({
                name: "failure",
                action: "transfer",
                message: error.message,
              });
            }}
          >
            <RelayDeposit.Content />
          </RelayDeposit>
        )}
      </div>
    </TrackingBoundary>
  );
}
